import React from "react";
import { motion, useInView } from "framer-motion";
import { Truck, Award, Scissors, ShieldCheck, Heart, Sparkles, Gift, Phone, RefreshCw, Star, Quote } from "lucide-react";
import { fileUrl } from "@/lib/api";

export const PROMISE_ICONS = { truck: Truck, award: Award, scissors: Scissors, shield: ShieldCheck, heart: Heart, sparkles: Sparkles, gift: Gift, phone: Phone, refresh: RefreshCw };

/* Counts up from 0 to the number in "5000+" / "25+" / "100%" when it scrolls into view */
export function CountUp({ value, className = "" }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const m = String(value || "").match(/^(\D*)([\d,]+)(.*)$/);
  const target = m ? Number(m[2].replace(/,/g, "")) : 0;
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    if (!inView || !m) return;
    const start = performance.now();
    const dur = 1600;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <span ref={ref} className={className}>{value}</span>;
  return <span ref={ref} className={className}>{m[1]}{n.toLocaleString("en-IN")}{m[3]}</span>;
}

/* NEW SECTION BELOW THE HERO — "The Avishka Navya Promise" (edit in Admin → Content → Home: Intro) */
export function HomeIntro({ data }) {
  if (!data || data.enabled === false) return null;
  const stats = (data.stats || []).filter((s) => s.value || s.label);
  const promises = (data.promises || []).filter((p) => p.title);
  return (
    <section className="relative overflow-hidden bg-[#FAF6EE]" data-testid="home-intro">
      {/* soft decorative shapes */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#D4AF37]/15 blur-3xl"/>
      <div className="pointer-events-none absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-[#0E4D34]/10 blur-3xl"/>
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(#0E4D34 1px, transparent 1px)", backgroundSize: "22px 22px" }}/>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {data.image && (
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
                        className="lg:col-span-5 relative mx-auto w-full max-w-md">
              <div className="absolute -inset-3 sm:-inset-4 rounded-[28px] border border-[#D4AF37]/60 translate-x-3 translate-y-3 sm:translate-x-5 sm:translate-y-5"/>
              <div className="relative aspect-[4/5] rounded-[24px] overflow-hidden shadow-[0_30px_60px_-25px_rgba(14,77,52,.5)]">
                <img src={fileUrl(data.image)} alt="" className="w-full h-full object-cover" loading="lazy"/>
              </div>
              {/* floating badge — text set in Admin → Website Content → Home: Intro (leave both empty to hide it) */}
              {(data.badge_label || data.badge_value) && (
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="absolute -bottom-6 -right-2 sm:-right-8 bg-white rounded-2xl shadow-xl px-6 py-4 border-l-4 border-[#D4AF37]" data-testid="intro-badge">
                  {data.badge_label && <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{data.badge_label}</div>}
                  {data.badge_value && <div className="text-xl font-semibold text-[#0E4D34] leading-none mt-1">{data.badge_value}</div>}
                </motion.div>
              )}
            </motion.div>
          )}

          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
                      className={data.image ? "lg:col-span-7" : "lg:col-span-12 text-center max-w-3xl mx-auto"}>
            {data.overline && (
              <div className={`flex items-center gap-3 mb-5 ${data.image ? "" : "justify-center"}`}>
                <span className="h-px w-10 bg-[#D4AF37]"/>
                <span className="text-xs font-bold uppercase tracking-[0.35em] text-[#0E4D34]">{data.overline}</span>
              </div>
            )}
            {data.title && <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-neutral-900 mb-6">{data.title}</h2>}
            {data.text && <p className="text-neutral-600 leading-relaxed text-base sm:text-lg mb-6 whitespace-pre-line">{data.text}</p>}
            {data.signature && (
              <div className="mb-8">
                <div className="font-serif italic text-2xl text-[#0E4D34]">— {data.signature}</div>
                {data.signature_role && <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 mt-1 ml-6">{data.signature_role}</div>}
              </div>
            )}
            {stats.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-0 sm:divide-x divide-[#0E4D34]/15 border-t border-[#0E4D34]/15 pt-6">
                {stats.map((s, i) => (
                  <div key={i} className="sm:px-3 text-center" data-testid={`intro-stat-${i}`}>
                    <CountUp value={s.value} className="block text-3xl sm:text-4xl font-semibold text-[#0E4D34] tracking-tight"/>
                    <div className="text-xs text-neutral-500 mt-1 leading-snug">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {promises.length > 0 && (
          <div className="mt-16 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {promises.map((p, i) => {
              const Icon = PROMISE_ICONS[p.icon] || Sparkles;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                            className="group bg-white/80 backdrop-blur rounded-2xl border border-[#0E4D34]/10 p-4 sm:p-6 text-center flex flex-col items-center hover:shadow-[0_18px_40px_-20px_rgba(14,77,52,.45)] hover:-translate-y-1 transition-all duration-300">
                  <span className="w-11 h-11 rounded-full bg-[#E8F3EE] text-[#0E4D34] flex items-center justify-center mb-3 group-hover:bg-[#0E4D34] group-hover:text-white transition-colors"><Icon size={19}/></span>
                  <div className="font-semibold text-neutral-900 text-sm sm:text-base">{p.title}</div>
                  {p.text && <div className="text-xs sm:text-sm text-neutral-500 mt-1">{p.text}</div>}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

/* Types the year one digit at a time when it scrolls into view (only the year is animated) */
export function TypewriterYear({ text, className = "" }) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const full = String(text || "");
  const [count, setCount] = React.useState(0);
  const [done, setDone] = React.useState(false);
  React.useEffect(() => {
    if (!inView) return;
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= full.length) { clearInterval(t); setTimeout(() => setDone(true), 1200); }
    }, 180);
    return () => clearInterval(t);
  }, [inView, full]);
  return (
    <span ref={ref} className={className} aria-label={full}>
      <span aria-hidden="true">{full.slice(0, count)}</span>
      {!done && <span aria-hidden="true" className="typewriter-caret">|</span>}
      {/* keeps the line height while empty */}
      {count === 0 && <span aria-hidden="true" className="invisible">0</span>}
    </span>
  );
}

const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

/* TESTIMONIALS — "Voices from our Sisterhood" (edit in Admin → Content → Home: Testimonials) */
export function Testimonials({ data }) {
  if (!data || data.enabled === false) return null;
  const items = (data.items || []).filter((t) => t.text);
  if (!items.length) return null;
  return (
    <section className="relative overflow-hidden bg-[#0E4D34] text-white py-20 sm:py-24" data-testid="testimonials">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#D4AF37]/10 blur-3xl"/>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="text-center mb-14">
          {data.overline && <div className="text-xs uppercase tracking-[0.4em] text-[#E9D8A6] mb-3">{data.overline}</div>}
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl">{data.title}</h2>
          <div className="mx-auto mt-5 flex items-center justify-center gap-2">
            <span className="h-px w-10 bg-[#D4AF37]/70"/><Star size={12} className="text-[#D4AF37]" fill="currentColor"/><span className="h-px w-10 bg-[#D4AF37]/70"/>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {items.map((t, i) => {
            const rating = Math.max(1, Math.min(5, Number(t.rating) || 5));
            return (
              <motion.figure key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                             className={`relative bg-white text-neutral-800 rounded-3xl p-7 sm:p-8 pt-10 shadow-[0_30px_60px_-30px_rgba(0,0,0,.6)] flex flex-col`}
                             data-testid={`testimonial-${i}`}>
                <span className="absolute -top-5 left-7 w-11 h-11 rounded-full bg-[#D4AF37] text-[#0E4D34] flex items-center justify-center shadow-lg"><Quote size={18} fill="currentColor"/></span>
                <div className="flex gap-0.5 mb-4 text-[#D4AF37]">{Array.from({ length: 5 }).map((_, k) => <Star key={k} size={15} fill={k < rating ? "currentColor" : "none"}/>)}</div>
                <blockquote className="text-[15px] sm:text-base leading-relaxed text-neutral-700 flex-1">"{t.text}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-neutral-100 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0E4D34] to-[#2D7A5B] text-white text-sm font-semibold flex items-center justify-center">{initials(t.name)}</span>
                  <div>
                    <div className="font-semibold text-neutral-900">{t.name}</div>
                    {t.role && <div className="text-xs text-neutral-500 uppercase tracking-wider">{t.role}</div>}
                  </div>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
        {data.button_text && data.button_link && (
          <div className="text-center mt-14">
            <a href={data.button_link} target="_blank" rel="noreferrer" data-testid="testimonials-view-more"
               className="inline-flex items-center rounded-full bg-white text-[#0E4D34] hover:bg-[#D4AF37] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors">
              {data.button_text}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
